import { useMemo, type ReactNode } from "react";
import { FaBook, FaPencilAlt, FaPercent, FaTimes, FaUndo } from "react-icons/fa";
import {
  EmployeeCaseKind,
  EmployeeCaseReportCategory,
  EmployeeCaseReportDay,
  EmployeeCaseReportRow,
  EmployeeCaseReportSummary,
  EmployeeRedesignReason,
} from "../../store/allocation/employeeDashboardSlice";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import { formatAdminDateTime } from "../admin/AdminPageShell";
import {
  DashboardTableCard,
  formatDisplayDate,
  portalTableCell,
  portalTableHead,
} from "./DashboardFilterControls";
import { currentOperationalDate, weekdayLabel } from "../../utils/operationalCalendar";
import {
  personWorkCreditTotal,
  Qc2CreditLabel,
  WorkCreditLabel,
} from "../../utils/workCreditDisplay";
import { employeeRowBucket } from "../../utils/redesignBuckets";
import { DASHBOARD } from "./dashboardTheme";

export const kindLabel = (kind: EmployeeCaseKind) => {
  if (kind === "redesign") return "External";
  if (kind === "internal_redesign") return "Internal";
  return "Live";
};

export const kindBadgeClass = (kind: EmployeeCaseKind) => {
  if (kind === "redesign") return "bg-amber-50 text-amber-800";
  if (kind === "internal_redesign") return "bg-violet-50 text-violet-800";
  return "bg-[#E8F6F8] text-[#1A6B78]";
};

const reportRowClass = (row: EmployeeCaseReportRow) => {
  const bucket = employeeRowBucket(row);
  if (bucket === "blunder") return "bg-red-50 hover:bg-red-100";
  if (bucket === "normal") return "bg-orange-50 hover:bg-orange-100";
  return "hover:bg-[#FBFBF9]";
};

const formatPct = (value: number | null | undefined) =>
  value == null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

const splitCategories = (raw?: string | null) =>
  String(raw || "")
    .split(",")
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);

export function CategoryChips({
  categories,
}: {
  categories: EmployeeCaseReportCategory[];
}) {
  if (!categories.length) {
    return <span className="text-xs text-[#8A8A85]">No redesign reasons yet</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {categories.map((cat) => (
        <span
          key={`${cat.kind}-${cat.category}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            cat.kind === "internal"
              ? "bg-violet-50 text-violet-800"
              : "bg-amber-50 text-amber-800"
          }`}
          title={`${cat.kind === "internal" ? "Internal" : "External"} · ${cat.category}`}
        >
          <span className="opacity-70">
            {cat.kind === "internal" ? "Int" : "Ext"}
          </span>
          {cat.category}
          <span className="opacity-70">×{cat.count}</span>
        </span>
      ))}
    </div>
  );
}

export function RedesignReasonList({
  redesigns,
}: {
  redesigns: EmployeeRedesignReason[];
}) {
  if (!redesigns.length) return null;
  return (
    <div className="mt-1.5 space-y-1">
      {redesigns.map((rd) => {
        const labels = splitCategories(rd.category);
        return (
          <div key={rd.case_id} className="flex flex-wrap items-center gap-1">
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${kindBadgeClass(rd.kind)}`}
            >
              {kindLabel(rd.kind)}
            </span>
            {labels.length ? (
              labels.map((label) => (
                <span
                  key={`${rd.case_id}-${label}`}
                  className="rounded-full bg-[#F5F5F2] px-2 py-0.5 text-[10px] font-medium text-[#4A4A48]"
                >
                  {label}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-[#8A8A85]">No category marked</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const sameEmployee = (a?: string | null, b?: string | null) =>
  String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** Filled-order units for one employee: their patients only, never the case total. */
export function filledOrderEmployeeUnits(
  row: EmployeeCaseReportRow,
  empName?: string | null,
) {
  const patients = row.filled_order_patients;
  if (!patients) return null;
  let marked = 0;
  let unmarked = 0;
  const names = new Set<string>();
  for (const patient of patients) {
    if (empName && !sameEmployee(patient.name, empName)) continue;
    if (patient.units <= 0) continue;
    names.add(patient.patient.trim().toLowerCase());
    if (patient.marked) marked += patient.units;
    else unmarked += patient.units;
  }
  return { marked, unmarked, total: marked + unmarked, patients: names.size };
}

export function designerUnitSplit(
  rows: EmployeeCaseReportRow[],
  empName?: string | null,
) {
  let marked = 0;
  let unmarked = 0;
  for (const row of rows) {
    const filled = filledOrderEmployeeUnits(row, empName);
    if (filled) {
      marked += filled.marked;
      unmarked += filled.unmarked;
      continue;
    }
    const units = personWorkCreditTotal(
      empName,
      row.designed_by,
      row.design_unit,
      row.designer_units,
      row.model_by,
      row.model_units,
    );
    if (units <= 0) continue;
    if (row.delivery_units_marked) marked += units;
    else unmarked += units;
  }
  return { marked, unmarked, total: marked + unmarked };
}

const nameListed = (value: string | null | undefined, empName?: string | null) => {
  const key = String(empName || "").trim().toLowerCase();
  if (!key) return false;
  return String(value || "")
    .split(",")
    .some((part) => part.trim().toLowerCase() === key);
};

const sumPositiveUnits = (map?: Record<string, number> | null) => {
  if (!map) return 0;
  return Object.values(map).reduce((sum, value) => {
    const units = Number(value);
    return sum + (Number.isFinite(units) && units > 0 ? units : 0);
  }, 0);
};

/** QC credit is the case design units plus model units — the figures beside the QC name. */
export function qcCaseUnits(row: EmployeeCaseReportRow) {
  const model = sumPositiveUnits(row.model_units);
  if (row.design_unit != null && Number(row.design_unit) > 0) {
    return Number(row.design_unit) + model;
  }
  const design = sumPositiveUnits(row.designer_units);
  if (design > 0 || model > 0) return design + model;
  return (row.filled_order_patients || []).reduce((sum, patient) => {
    const units = Number(patient.units);
    return sum + (Number.isFinite(units) && units > 0 ? units : 0);
  }, 0);
}

export type WorkCreditBox = {
  cases: number;
  patients: number;
  modelOnly: number;
  marked: number;
  unmarked: number;
};

export type EmployeeWorkSplit = {
  liveDesigned: WorkCreditBox;
  liveQc: WorkCreditBox;
  redesignDesigned: WorkCreditBox;
  redesignQc: WorkCreditBox;
};

const emptyWorkBox = (): WorkCreditBox => ({
  cases: 0,
  patients: 0,
  modelOnly: 0,
  marked: 0,
  unmarked: 0,
});

const addUnits = (box: WorkCreditBox, units: number, marked: boolean) => {
  if (units <= 0) return;
  if (marked) box.marked += units;
  else box.unmarked += units;
};

/** Live and redesign, each split into cases he designed and cases he QC'd. */
export function employeeWorkSplit(
  rows: EmployeeCaseReportRow[],
  empName?: string | null,
): EmployeeWorkSplit {
  const liveDesigned = emptyWorkBox();
  const liveQc = emptyWorkBox();
  const redesignDesigned = emptyWorkBox();
  const redesignQc = emptyWorkBox();

  for (const row of rows) {
    const live = row.kind === "live";
    const designedBox = live ? liveDesigned : redesignDesigned;
    const qcBox = live ? liveQc : redesignQc;
    const filled = filledOrderEmployeeUnits(row, empName);
    const designed =
      !row.model_only && nameListed(row.designed_by, empName);

    if (filled) {
      if (filled.patients > 0) {
        designedBox.cases += 1;
        designedBox.patients += filled.patients;
        if (row.model_only) designedBox.modelOnly += 1;
        designedBox.marked += filled.marked;
        designedBox.unmarked += filled.unmarked;
      }
    } else if (designed || row.model_only) {
      const units = personWorkCreditTotal(
        empName,
        row.designed_by,
        row.design_unit,
        row.designer_units,
        row.model_by,
        row.model_units,
      );
      if (designed || units > 0) {
        designedBox.cases += 1;
        if (row.model_only) designedBox.modelOnly += 1;
        addUnits(designedBox, units, row.delivery_units_marked);
      }
    }

    if (nameListed(row.qc2_by, empName)) {
      qcBox.cases += 1;
      addUnits(qcBox, qcCaseUnits(row), row.delivery_units_marked);
    }
  }

  return { liveDesigned, liveQc, redesignDesigned, redesignQc };
}

export function DesignerUnitsTotal({
  marked,
  unmarked,
}: {
  marked: number;
  unmarked: number;
}) {
  const total = marked + unmarked;
  return (
    <p className="text-sm text-[#4A4A48]">
      Units done:{" "}
      {unmarked > 0 && marked > 0 ? (
        <>
          <span className="font-semibold text-[#2C2C2C]">{marked}</span>
          <span className="text-[#8A8A85]"> + </span>
          <span
            className="font-semibold text-red-600"
            title="Not marked by the unit allocator"
          >
            {unmarked}
          </span>
        </>
      ) : (
        <span
          className={`font-semibold ${unmarked > 0 ? "text-red-600" : "text-[#2C2C2C]"}`}
          title={
            unmarked > 0 ? "Not marked by the unit allocator" : "Marked by the unit allocator"
          }
        >
          {total}
        </span>
      )}
      {unmarked > 0 ? (
        <span className="text-xs text-red-600"> · red is not marked by the unit allocator</span>
      ) : null}
    </p>
  );
}

function WorkUnitsFigure({
  marked,
  unmarked,
}: {
  marked: number;
  unmarked: number;
}) {
  const total = marked + unmarked;
  if (unmarked > 0 && marked > 0) {
    return (
      <p className="mt-1 text-lg font-bold tabular-nums leading-none">
        <span className="text-[#2C2C2C]">{marked}</span>
        <span className="font-normal text-[#8A8A85]"> + </span>
        <span className="text-red-600" title="Not marked by the unit allocator">
          {unmarked}
        </span>
      </p>
    );
  }
  const unmarkedOnly = unmarked > 0;
  return (
    <p
      className={`mt-1 text-lg font-bold tabular-nums leading-none ${
        unmarkedOnly ? "text-red-600" : total > 0 ? "text-[#2C2C2C]" : "text-[#8A8A85]"
      }`}
      title={
        unmarkedOnly
          ? "Not marked by the unit allocator"
          : total > 0
            ? "Marked by the unit allocator"
            : undefined
      }
    >
      {total}
    </p>
  );
}

export function TotalWorkCard({
  split,
  scopeLabel,
}: {
  split: EmployeeWorkSplit;
  scopeLabel: string;
}) {
  const boxes: Array<{
    label: string;
    hint: string;
    box: WorkCreditBox;
    accent: string;
  }> = [
    {
      label: "Live designed",
      hint: "Live cases he designed, and the units from that work",
      box: split.liveDesigned,
      accent: DASHBOARD.casesCredited,
    },
    {
      label: "Live QC",
      hint: "Live cases he QC'd, and the units from that QC",
      box: split.liveQc,
      accent: DASHBOARD.qcUnitsCard,
    },
    {
      label: "Redesign designed",
      hint: "Redesigns he designed, and the units from that work",
      box: split.redesignDesigned,
      accent: DASHBOARD.designUnitsCard,
    },
    {
      label: "Redesign QC",
      hint: "Redesigns he QC'd, and the units from that QC",
      box: split.redesignQc,
      accent: DASHBOARD.darkTeal,
    },
  ];
  const anyUnmarked = boxes.some((item) => item.box.unmarked > 0);
  const markedUnits = boxes.reduce((sum, item) => sum + item.box.marked, 0);
  const unmarkedUnits = boxes.reduce((sum, item) => sum + item.box.unmarked, 0);
  const allUnits = markedUnits + unmarkedUnits;

  return (
    <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {boxes.map((item) => {
          const { box } = item;
          return (
            <div
              key={item.label}
              className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] px-4 py-3"
              style={{ borderLeftWidth: 4, borderLeftColor: item.accent }}
              title={item.hint}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
                {item.label}
              </p>
              <p className="mt-2 text-3xl font-bold tabular-nums leading-none text-[#2C2C2C]">
                {box.cases}
              </p>
              <p className="mt-1 text-[11px] text-[#6B6B66]">
                {box.cases === 1 ? "case" : "cases"}
                {box.patients > 0
                  ? ` · ${box.patients} filled-order patient${box.patients === 1 ? "" : "s"}`
                  : ""}
                {box.modelOnly > 0
                  ? ` · ${box.modelOnly} model-only`
                  : ""}
              </p>
              <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
                Units
              </p>
              <WorkUnitsFigure marked={box.marked} unmarked={box.unmarked} />
            </div>
          );
        })}
        <div
          className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] px-4 py-3"
          style={{ borderLeftWidth: 4, borderLeftColor: DASHBOARD.header }}
          title="Marked and unmarked units from live design, live QC, redesign design, and redesign QC"
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
            All units
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums leading-none text-[#2C2C2C]">
            {allUnits}
          </p>
          <p className="mt-1 text-[11px] text-[#6B6B66]">
            Live, redesign, design, and QC
          </p>
          <div className="mt-3 flex flex-col gap-1 text-sm font-semibold tabular-nums">
            <p className="text-[#2C2C2C]">
              {markedUnits}
              <span className="ml-1.5 text-[11px] font-medium text-[#6B6B66]">marked</span>
            </p>
            <p className={unmarkedUnits > 0 ? "text-red-600" : "text-[#2C2C2C]"}>
              {unmarkedUnits}
              <span className="ml-1.5 text-[11px] font-medium text-[#6B6B66]">unmarked</span>
            </p>
          </div>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-[#8A8A85]">
        {scopeLabel}. A case that was both designed and QC'd is listed in both boxes, with the units for each.
        {anyUnmarked ? (
          <span className="text-red-600"> Red is not marked by the unit allocator.</span>
        ) : (
          " Units are marked by the unit allocator."
        )}
      </p>
    </div>
  );
}

export function OverviewSummaryCards({
  summary,
  blunderPct,
  blunderCount,
}: {
  summary: EmployeeCaseReportSummary;
  blunderPct: number | null;
  blunderCount: number;
}) {
  const cards = [
    {
      label: "Cases designed",
      value: summary.designed_cases,
      hint: "Live cases designed in this period",
      bg: DASHBOARD.casesCredited,
      icon: <FaBook />,
    },
    {
      label: "Came back",
      value: summary.came_back,
      hint: `${summary.came_back_external} external · ${summary.came_back_internal} internal · designed or QC'd`,
      bg: DASHBOARD.terracotta,
      icon: <FaUndo />,
    },
    {
      label: "Redesign %",
      value: formatPct(summary.redesign_pct),
      hint: "Came back ÷ cases designed or QC'd",
      bg: DASHBOARD.purple,
      icon: <FaPercent />,
    },
    {
      label: "Blunder %",
      value: formatPct(blunderPct),
      hint: `${blunderCount} blunder redesign${blunderCount === 1 ? "" : "s"} ÷ cases designed or QC'd`,
      bg: "#B42318",
      icon: <FaPercent />,
    },
    {
      label: "Redesigns designed",
      value: summary.designed_redesigns,
      hint: "Internal + external redesign cases designed",
      bg: DASHBOARD.qcUnitsCard,
      icon: <FaPencilAlt />,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 [&>*]:min-w-0">
      {cards.map((card) => (
        <div
          key={card.label}
          className="relative overflow-hidden rounded-2xl p-4 text-white shadow-sm"
          style={{ backgroundColor: card.bg }}
        >
          <div className="absolute right-4 top-4 text-lg text-white/70">{card.icon}</div>
          <p className="pr-8 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/80">
            {card.label}
          </p>
          <p className="mt-2 text-2xl font-bold tabular-nums sm:text-3xl">{card.value}</p>
          <p className="mt-1 text-[11px] leading-snug text-white/80">{card.hint}</p>
        </div>
      ))}
    </div>
  );
}

export function DailyCaseReportTable({
  days,
  selectedDate,
  onSelectDate,
}: {
  days: EmployeeCaseReportDay[];
  selectedDate: string | "all";
  onSelectDate: (date: string | "all") => void;
}) {
  return (
    <DashboardTableCard title="Everyday cases & redesign % (noon IST → next noon)">
      <table className="min-w-full text-left">
        <thead className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
          <tr>
            <th className={portalTableHead}>Day</th>
            <th className={portalTableHead}>Designed</th>
            <th className={portalTableHead}>QC</th>
            <th className={portalTableHead}>Came back</th>
            <th className={portalTableHead}>External</th>
            <th className={portalTableHead}>Internal</th>
            <th className={portalTableHead}>Redesign %</th>
            <th className={portalTableHead}>Reasons</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0EC]">
          {!days.length ? (
            <tr>
              <td colSpan={8} className={`${portalTableCell} text-sm text-[#8A8A85]`}>
                No designed or QC'd live cases in this period.
              </td>
            </tr>
          ) : (
            days.map((day) => {
              const active = selectedDate === day.date;
              const tracked = day.tracked_cases ?? day.designed_cases;
              const empty = tracked === 0;
              const isToday = day.date === currentOperationalDate();
              return (
                <tr
                  key={day.date}
                  className={`cursor-pointer ${
                    active
                      ? "bg-[#E8F6F8]"
                      : empty
                        ? "bg-[#FBFBF9] hover:bg-[#F5F5F2]"
                        : "hover:bg-[#FBFBF9]"
                  }`}
                  onClick={() => onSelectDate(active ? "all" : day.date)}
                >
                  <td className={`${portalTableCell} text-sm font-semibold text-[#2C2C2C]`}>
                    <div className="flex items-center gap-2">
                      <span>{weekdayLabel(day.date)}</span>
                      <span>{formatDisplayDate(day.date)}</span>
                      {isToday ? (
                        <span className="rounded-full bg-[#345952]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#345952]">
                          Today
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className={`${portalTableCell} text-sm tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {day.designed_cases}
                  </td>
                  <td className={`${portalTableCell} text-sm tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {day.qc_cases ?? 0}
                  </td>
                  <td className={`${portalTableCell} text-sm tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {day.came_back}
                  </td>
                  <td className={`${portalTableCell} text-sm tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {day.came_back_external}
                  </td>
                  <td className={`${portalTableCell} text-sm tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {day.came_back_internal}
                  </td>
                  <td className={`${portalTableCell} text-sm font-semibold tabular-nums ${empty ? "text-[#8A8A85]" : ""}`}>
                    {formatPct(day.redesign_pct)}
                  </td>
                  <td className={`${portalTableCell}`}>
                    {empty ? (
                      <span className="text-xs text-[#8A8A85]">No cases</span>
                    ) : (
                      <CategoryChips categories={day.categories} />
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </DashboardTableCard>
  );
}

const PersonTimeCell = ({
  name,
  at,
}: {
  name?: ReactNode;
  at?: string | null;
}) => (
  <td className={`${portalTableCell} align-top text-sm`}>
    <div className="font-medium text-[#2C2C2C]">
      {name == null || name === "" ? "—" : name}
    </div>
    <div className="mt-0.5 text-[11px] text-[#8A8A85]">
      {at ? formatAdminDateTime(at) : "—"}
    </div>
  </td>
);

export function EmployeeLifecycleTable({
  rows,
  loading,
  emptyMessage,
  showCameBack = true,
  empName,
  onOpenReference,
  referenceLoadingId,
}: {
  rows: EmployeeCaseReportRow[];
  loading?: boolean;
  emptyMessage: string;
  showCameBack?: boolean;
  empName?: string | null;
  onOpenReference?: (row: EmployeeCaseReportRow) => void;
  referenceLoadingId?: string | null;
}) {
  const showUnits = Boolean(String(empName || "").trim());
  const colSpan = (showCameBack ? 5 : 3) + (showUnits ? 1 : 0);

  return (
    <DashboardTableCard>
      <table className="min-w-full text-left">
        <thead className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
          <tr>
            <th className={portalTableHead}>Case ID</th>
            {showUnits ? (
              <th className={portalTableHead} title="This designer's design and model units">
                Units
              </th>
            ) : null}
            {showCameBack ? <th className={portalTableHead}>Came back</th> : null}
            <th className={portalTableHead}>Design</th>
            <th className={portalTableHead}>QC</th>
            {showCameBack ? <th className={portalTableHead}>Old logs</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0EC]">
          {loading ? (
            <tr>
              <td colSpan={colSpan} className={`${portalTableCell} text-sm text-[#8A8A85]`}>
                Loading cases…
              </td>
            </tr>
          ) : !rows.length ? (
            <tr>
              <td colSpan={colSpan} className={`${portalTableCell} text-sm text-[#8A8A85]`}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const refBusy = referenceLoadingId === row.live_case_id;
              const canOpen = Boolean(row.has_reference || row.came_back);
              const filledUnits = showUnits
                ? filledOrderEmployeeUnits(row, empName)
                : null;
              const units = filledUnits
                ? filledUnits.total
                : showUnits
                  ? personWorkCreditTotal(
                      empName,
                      row.designed_by,
                      row.design_unit,
                      row.designer_units,
                      row.model_by,
                      row.model_units,
                    )
                  : 0;
              const unmarkedUnits = filledUnits
                ? filledUnits.unmarked
                : row.delivery_units_marked
                  ? 0
                  : units;
              const markedUnits = units - unmarkedUnits;
              return (
                <tr key={`${row.kind}-${row.case_id}`} className={reportRowClass(row)}>
                  <td className={`${portalTableCell} align-top`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <a
                        href={caseHrefFromAllocationId(row.case_id)}
                        className="font-semibold text-[#345952] hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.case_id}
                      </a>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${kindBadgeClass(row.kind)}`}
                      >
                        {kindLabel(row.kind)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#8A8A85]">
                      {row.created_at ? formatAdminDateTime(row.created_at) : "—"}
                    </div>
                    {row.kind !== "live" && row.category ? (
                      <div className="mt-1 text-[11px] leading-snug text-[#6B6B66]">
                        {row.category}
                      </div>
                    ) : null}
                    {row.kind === "live" ? (
                      <RedesignReasonList redesigns={row.redesigns || []} />
                    ) : null}
                  </td>
                  {showUnits ? (
                    <td className={`${portalTableCell} align-top text-sm font-semibold`}>
                      {units <= 0 ? (
                        <span className="text-[#8A8A85]">—</span>
                      ) : unmarkedUnits > 0 && markedUnits > 0 ? (
                        <span>
                          <span className="text-[#2C2C2C]">{markedUnits}</span>
                          <span className="font-normal text-[#8A8A85]"> + </span>
                          <span className="text-red-600" title="Not marked by the unit allocator">
                            {unmarkedUnits}
                          </span>
                        </span>
                      ) : (
                        <span
                          className={unmarkedUnits > 0 ? "text-red-600" : "text-[#2C2C2C]"}
                          title={
                            unmarkedUnits > 0
                              ? "Not marked by the unit allocator"
                              : "Marked by the unit allocator"
                          }
                        >
                          {units}
                        </span>
                      )}
                    </td>
                  ) : null}
                  {showCameBack ? (
                    <td className={`${portalTableCell} align-top text-sm`}>
                      {row.kind === "live" &&
                      (!row.model_only || nameListed(row.qc2_by, empName)) ? (
                        row.came_back ? (
                          <div>
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                              Yes
                            </span>
                            <div className="mt-1 text-[11px] text-[#8A8A85]">
                              {row.came_back_external ? "External" : ""}
                              {row.came_back_external && row.came_back_internal
                                ? " · "
                                : ""}
                              {row.came_back_internal ? "Internal" : ""}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-[#8A8A85]">No</span>
                        )
                      ) : (
                        <span className="text-[11px] text-[#8A8A85]">—</span>
                      )}
                    </td>
                  ) : null}
                  <PersonTimeCell
                    name={
                      <WorkCreditLabel
                        designedBy={row.designed_by}
                        designUnit={row.design_unit}
                        designerUnits={row.designer_units}
                        modelBy={row.model_by}
                        modelUnits={row.model_units}
                      />
                    }
                    at={row.designed_at}
                  />
                  <PersonTimeCell
                    name={
                      <Qc2CreditLabel
                        name={row.qc2_by}
                        designUnit={row.design_unit}
                        modelUnits={row.model_units}
                      />
                    }
                    at={row.qc2_at}
                  />
                  {showCameBack ? (
                    <td className={`${portalTableCell} align-middle`}>
                      <button
                        type="button"
                        disabled={!canOpen || refBusy || !onOpenReference}
                        onClick={() => onOpenReference?.(row)}
                        className="rounded-lg border border-[#DDDDD8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#4A4A48] transition enabled:hover:border-[#345952] enabled:hover:text-[#345952] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {refBusy ? "Loading…" : "Old logs"}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </DashboardTableCard>
  );
}

export function HistoryModal({
  liveCaseId,
  rows,
  loading,
  error,
  onClose,
}: {
  liveCaseId: string;
  rows: EmployeeCaseReportRow[];
  loading: boolean;
  error?: string | null;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close reference modal"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#E8E8E4] px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-[#2C2C2C]">
              Old logs — {liveCaseId}
            </h2>
            <p className="mt-0.5 text-xs text-[#8A8A85]">
              All live, internal, and external redesign allocations for this case
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#8A8A85] hover:bg-[#F5F5F2] hover:text-[#4A4A48]"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="overflow-auto p-4">
          {error ? (
            <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          ) : null}
          <EmployeeLifecycleTable
            rows={rows}
            loading={loading}
            emptyMessage="No allocation records found for this case."
            showCameBack={false}
          />
        </div>
      </div>
    </div>
  );
}

export function PeriodCategoryCard({
  categories,
}: {
  categories: EmployeeCaseReportCategory[];
}) {
  const grouped = useMemo(() => {
    const internal = categories.filter((c) => c.kind === "internal");
    const external = categories.filter((c) => c.kind === "external");
    return { internal, external };
  }, [categories]);

  return (
    <div className="grid gap-3 md:grid-cols-2 [&>*]:min-w-0">
      <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
          External redesign reasons
        </p>
        <div className="mt-3">
          <CategoryChips categories={grouped.external} />
        </div>
      </div>
      <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
          Internal redesign reasons
        </p>
        <div className="mt-3">
          <CategoryChips categories={grouped.internal} />
        </div>
      </div>
    </div>
  );
}
